import {
  StyledNotification,
  StyledSubject,
  StyledMessage,
  StyledTime,
  StyledTimeRow,
  StyledTitleRow,
  StyledNotificationTitle,
  StyledDefaultCDKIconText,
  StyledNotificationContainer,
  ProductContentIcon,
  StyledNotificationDot,
  NotificationItemMarkReadButton,
  StyledNotificationMiddleDeleteButton,
} from './styles';
import { getTimeDiffText } from '../../util/timeUtils';
import { useTranslation } from 'react-i18next';
import { Divider, StatusIndicator, STATUS_INDICATOR_VARIANTS } from 'cdk-radial';
import { IconClose, IconButton } from 'cdk-radial';
import { getLocalizationValues } from "../../v2/utils/utils";
import useProfileStore from "../core/UserProfileStore";
import { useTheme } from '../theme/useTheme';

const NOTIFICATION_TYPE_TAGS: Record<string, { labelKey: string; defaultLabel: string; variant: string }> = {
  outage: { labelKey: 'notification.type.alert', defaultLabel: 'Alert', variant: STATUS_INDICATOR_VARIANTS.NEGATIVE },
  release: { labelKey: 'notification.type.feature', defaultLabel: 'Feature', variant: STATUS_INDICATOR_VARIANTS.INFORMATION },
};
const DEFAULT_NOTIFICATION_TYPE_TAG = { labelKey: 'notification.type.activity', defaultLabel: 'Activity', variant: STATUS_INDICATOR_VARIANTS.POSITIVE };

const NotificationTypeTag = ({ type }: { type?: string }) => {
  const { t } = useTranslation();
  const tag = (type && NOTIFICATION_TYPE_TAGS[type]) || DEFAULT_NOTIFICATION_TYPE_TAG;
  return (
    <div style={{ flexShrink: 0, marginTop: '-5px' }}>
      <StatusIndicator label={t(tag.labelKey, { defaultValue: tag.defaultLabel }) as string} variant={tag.variant} />
    </div>
  );
};
/**
 * Interfaces
 */
export interface INotification {
  messageId?: string;
  title?: string;
  subject?: string;
  body?: string;
  titleLocale?: {enCA: string, frCA: string};
  subjectLocale?: {enCA: string, frCA: string};
  bodyLocale?: {enCA: string, frCA: string};
  externalUrlLocale?: {enCA: string, frCA: string};
  createdDate?: string;
  receivedDate?: string;
  read?: boolean;
  externalUrl?: string;
  application?: string;
  source?: string;
  type?: string;
  deleted?: boolean;
  ctaUrl?: string;
  target?: any;
  includeTargets?: boolean;
}

const Notification = (props: {
  notification: INotification;
  applications: any;
  onSelect?: Function;
  onDelete?: Function;
  onToggleRead?: Function;
  userProfile?: any
}): JSX.Element => {
  const { notification, onSelect, onDelete, onToggleRead, applications } = props;
  const locale = useProfileStore((state) => state.locale);
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const localizedValues = getLocalizationValues(locale, notification);
  const appIconColor = applications.find(
    (app: any) => app.name === notification.application
  )?.icon;
  const appIconText = applications.find(
    (app: any) => app.name === notification.application
  )?.iconText;
  const defaultCDKIconText = (
    <StyledDefaultCDKIconText fontSize="15px">
      {' '}
      {'CDK'}{' '}
    </StyledDefaultCDKIconText>
  );
  return (
    <StyledNotification
      data-testid="notification-item"
      onClick={() => onSelect && onSelect(notification)}
      onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            onSelect && onSelect(notification)
          }
        }}
      tabIndex={0}
    >
      {!notification.read && <StyledNotificationDot></StyledNotificationDot>}
      {'outage' !== notification.type && (
        <StyledNotificationMiddleDeleteButton>
          <IconButton
            dataTestId={'notification-item-delete'}
            hideText
            icon={<IconClose style={{ width: '18px' }} />}
            onClick={(e: any) => {
              e.stopPropagation();
              onDelete && onDelete(notification);
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                onDelete && onDelete(notification);
              }
            }}
            text="Delete"
          />
        </StyledNotificationMiddleDeleteButton>
      )}
      <StyledNotificationContainer>
        <StyledTitleRow>
          <StyledNotificationTitle>
            <ProductContentIcon iconColor={appIconColor} data-testid="app-icon">
              {appIconText || defaultCDKIconText}
            </ProductContentIcon>
            <StyledSubject read={notification.read!}>
              {localizedValues.title}{' '}
            </StyledSubject>
          </StyledNotificationTitle>
          <NotificationTypeTag type={notification.type} />
        </StyledTitleRow>
        <StyledMessage>{localizedValues.subject}</StyledMessage>
        <StyledTimeRow>
          <StyledTime>
            {getTimeDiffText(notification.receivedDate as string, t)}
          </StyledTime>
          {onToggleRead && (
            <NotificationItemMarkReadButton
              data-testid="notification-item-toggle-read"
              onClick={(e: any) => {
                e.stopPropagation();
                onToggleRead(notification);
              }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleRead(notification);
                }
              }}
              text={notification.read ? t('notification.sheet.markUnread') : t('notification.sheet.markRead')}
              darkMode={darkMode}
              tabIndex={0}
            />
          )}
        </StyledTimeRow>
      </StyledNotificationContainer>
      <Divider></Divider>
    </StyledNotification>
  );
};
export default Notification;
